<script lang="ts">
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { meta, router } from "tinro";

    const route = meta();

    import { sendGAEvent } from "../lib/utilities/ga.js";
    import { derived } from "svelte/store";

    import { databases, filteredDatabases, isLoadingDatabases } from "../lib/data/databases.js";
    import { classes } from "../lib/data/classes.js";
    import { selectedClass } from "../lib/data/stores.js";

    import FilterBar from "../lib/components/FilterBar.svelte";
    import Select from "../lib/components/Select.svelte";
    import Button from "../lib/components/Button.svelte";
    import SearchSummary from "../lib/components/SearchSummary.svelte";
    import DatabaseList from "../lib/components/DatabaseList.svelte";
    import LibguideLink from "../lib/components/LibguideLink.svelte";
    import TextBlock from "../lib/components/TextBlock.svelte";
    import NoResults from "../lib/components/NoResults.svelte";
    import Row from "../lib/components/Row.svelte";
    import Spinner from "../lib/components/Spinner.svelte";

    export let content = "";

    const parentClasses = derived(classes, ($classes) => {
        return $classes.map((classObj) => {
            return {
                id: classObj.id,
                name: classObj.name,
                description: classObj.description
            };
        });
    });

    let showSummary = false;
    let databasesNotFound = false;
    let mounted = false;

    onMount(async () => {
        const selected = router.location.query.get("selectedClass");
        console.log("selected: ", selected);

        if (selected) {
            if (typeof selected === "string") {
                selectedClass.set(decodeURIComponent(selected));
            }
        } else {
            selectedClass.set("");
        }

        // Wait for data to finish loading once, then filter
        isLoadingDatabases.subscribe(async (isLoading) => {
            if (!isLoading) {
                const allDBs = get(databases);
                filteredDatabases.set(allDBs);
                filterByClass();
            }
        });

        mounted = true;
    });

    // Keep this subscription so filteredDatabases is populated initially
    databases.subscribe((dbs) => {
        filteredDatabases.set(dbs);
    });

    // Whenever the component is ready (mounted),
    // update query params if $selectedClass changes.
    $: {
        if (mounted) {
            filterByClass();
            if ($selectedClass) {
                console.log("selectedClass reactive: ", $selectedClass);
                router.location.query.set("selectedClass", encodeURIComponent($selectedClass));
            } else {
                router.location.query.delete("selectedClass");
            }
        }
    }

    function filterByClass() {
        const allDBs = get(databases);
        filteredDatabases.set(allDBs);
        databasesNotFound = false;

        const currentClass = get(selectedClass);

        if (currentClass) {
            filteredDatabases.update((currentDatabases) => {
                const filtered = currentDatabases.filter((database) => {
                    return (
                        database.classes &&
                        database.classes.some((dbclass) => dbclass.name === currentClass)
                    );
                });

                if (filtered.length > 0) {
                    showSummary = true;
                    databasesNotFound = false;
                    return filtered;
                } else {
                    showSummary = true;
                    databasesNotFound = true;
                    return [];
                }
            });

            sendGAEvent("ByClass", "class_filter", currentClass, route.match);
        } else {
            filteredDatabases.set(allDBs);
            databasesNotFound = false;
        }
    }

    function clearFilter() {
        selectedClass.set("");
        showSummary = false;
        filterByClass();

        sendGAEvent("ByClass", "clear_filter", "All filters cleared", route.match);
    }

    // Identify matching class in parentClasses
    $: matchingClass = $parentClasses.find((c) => c.name === $selectedClass);

    // Debug logs
    $: {
        console.log("filtered: ", $filteredDatabases, "count: ", $filteredDatabases.length);
        console.log("selectedClass: ", $selectedClass);
        console.log("matchingClass: ", matchingClass);
    }
</script>

<FilterBar parent_id="by-class" showCopyUrl={true}>
    <TextBlock
            classes="mb-3"
            title={content.label}
            text={content.heading}
    />
    <form id="databaseFilter" class="form-inline d-flex flex-nowrap">
        <Select
                options={$parentClasses}
                id="filterControl"
                bind:value={$selectedClass}
                optionsname="Class"
                on:change={filterByClass}
        />
        <Button
                on:click={clearFilter}
                label="clear"
                classes="btn btn-info small"
        />
    </form>
</FilterBar>

{#if showSummary}
    {#if $isLoadingDatabases}
        <Spinner />
    {:else}
        <Row>
            <SearchSummary
                    on:copyToClipboard
                    count={$filteredDatabases.length}
                    searchTerm={$selectedClass}
            />
        </Row>
    {/if}
{/if}

{#if matchingClass && $selectedClass}
    <LibguideLink url={matchingClass.description} classname="{$selectedClass}" />
{/if}

{#if $filteredDatabases.length > 0}
    <DatabaseList
            on:openDialog
            bind:databases={$filteredDatabases}
    />
{:else}
    <NoResults />
{/if}

<style></style>
